import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loan } from '@shared/api/@types/loans';
import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { DateTime } from 'luxon';

import { useMarkets } from '../../../../../../lib/markets/hooks/useMarkets';

export const useSingleLoanSummaryCard = (loanData?: Loan) => {
  const { getAssetByCode } = useMarkets();
  const { t } = useTranslation('loans');

  const loanType = useMemo(() => {
    switch (loanData?.repaymentType) {
      case 'LINE_OF_CREDIT':
        return 'singleLoan.loanSummaryCard.loanType.lineOfCredit';
      case 'FIXED_TERM':
        return 'singleLoan.loanSummaryCard.loanType.fixedTerm';
      default:
        return undefined;
    }
  }, [loanData?.repaymentType]);

  const loanId = useMemo(() => loanData?.loanId, [loanData?.loanId]);

  const principal = useMemo(() => {
    if (!loanData?.borrowPrincipalAmount) return undefined;

    const fiatCurrency = getAssetByCode(loanData?.fiatCurrency ? loanData.fiatCurrency : 'AUD');

    return formatCurrency(Big(loanData.borrowPrincipalAmount ?? 0).toFixed(8), fiatCurrency);
  }, [loanData?.borrowPrincipalAmount, loanData?.fiatCurrency, getAssetByCode]);

  const interestRate = useMemo(() => {
    if (!loanData?.interestRate) return undefined;

    const ratePercentage = Big(loanData?.interestRate).mul(100).toFixed(2);

    // TODO: Do we need to consider non fixed rate here?
    return `${ratePercentage}% (Fixed Rate)`;
  }, [loanData?.interestRate]);

  // TODO: should this be hardcoded or is that value set somewhere
  const comparisonRate = useMemo(() => {
    // TODO change to comparison rate if possible
    if (!loanData?.interestRate) return undefined;
    return '11.50%';
  }, [loanData?.interestRate]);

  const loanTerm = useMemo(() => {
    if (!loanData?.termInDays) {
      return undefined;
    }

    // TODO: rework this to fit other loan terms
    const term = loanData.termInDays.eq(365) ? '12 months' : `${loanData?.termInDays} days`;
    return `Up to ${term}`;
  }, [loanData?.termInDays]);

  const loanTermDates: string | undefined = useMemo(() => {
    if (!loanData?.createdAt || !loanData?.validUntil) {
      return undefined;
    }

    const formatDate = (dateTime: DateTime): string => dateTime.toFormat('MMMM dd, yyyy');
    const formattedStartDate = formatDate(loanData.createdAt);
    const formattedEndDate = formatDate(loanData.validUntil);

    return `${formattedStartDate} - ${formattedEndDate}`;
  }, [loanData?.createdAt, loanData?.validUntil]);

  const outstandingPrincipal = useMemo(() => {
    if (!loanData?.principalLeft) return undefined;

    const fiatCurrency = getAssetByCode(loanData?.fiatCurrency ? loanData.fiatCurrency : 'AUD');

    return formatCurrency(Big(loanData?.principalLeft ?? 0).toFixed(8), fiatCurrency);
  }, [loanData?.principalLeft, getAssetByCode, loanData?.fiatCurrency]);

  //TODO: implement when available
  const originationFee = useMemo(() => undefined, []);

  //TODO: implement when available
  const totalInterestAccrued = useMemo(() => undefined, []);

  //TODO: implement when available
  const totalOutstandingBalance = useMemo(() => undefined, []);

  return {
    loanType: t(loanType as unknown as TemplateStringsArray),
    loanId,
    principal,
    interestRate,
    comparisonRate,
    loanTermDates,
    loanTerm,
    outstandingPrincipal,
    totalInterestAccrued,
    totalOutstandingBalance,
    originationFee,
  };
};
